import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import ContactPage from "@/pages/ContactPage";
import SetupWizard from "@/pages/SetupWizard";
import DashboardLayout from "@/components/layouts/DashboardLayout";
import CompanyDashboard from "@/pages/dashboard/CompanyDashboard";
import InvoicesPage from "@/pages/dashboard/InvoicesPage";
import CreateInvoicePage from "@/pages/dashboard/CreateInvoicePage";
import ClientsPage from "@/pages/dashboard/ClientsPage";
import CreditNotesPage from "@/pages/dashboard/CreditNotesPage";
import PaymentComplementsPage from "@/pages/dashboard/PaymentComplementsPage";
import ReportsPage from "@/pages/dashboard/ReportsPage";
import SettingsPage from "@/pages/dashboard/SettingsPage";
import SubscriptionPage from "@/pages/dashboard/SubscriptionPage";
import ChatPage from "@/pages/dashboard/ChatPage";
import PdfCustomizerPage from "@/pages/dashboard/PdfCustomizerPage";
import AnalyticsPage from "@/pages/dashboard/AnalyticsPage";
import TeamPage from "@/pages/dashboard/TeamPage";
import RecurringPage from "@/pages/dashboard/RecurringPage";
import ApiKeysPage from "@/pages/dashboard/ApiKeysPage";
import SupportPage from "@/pages/dashboard/SupportPage";
import TaxConfigPage from "@/pages/dashboard/TaxConfigPage";
import CertificatesPage from "@/pages/dashboard/CertificatesPage";
import MultiRfcPage from "@/pages/dashboard/MultiRfcPage";
import QuotesPage from "@/pages/dashboard/QuotesPage";
import CartaPortePage from "@/pages/dashboard/CartaPortePage";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminCompaniesPage from "@/pages/admin/AdminCompaniesPage";
import AdminTicketsPage from "@/pages/admin/AdminTicketsPage";
import AdminConfigPage from "@/pages/admin/AdminConfigPage";
import ReferralProgram from "@/pages/ReferralProgram";
import ApiDashboard from "@/pages/ApiDashboard";
import DeveloperPortal from "@/pages/DeveloperPortal";

const ProtectedRoute = ({ children, requireAdmin = false, requireOnboarding = true }) => {
  const { user, loading, onboardingCompleted } = useAuth();
  
  // Check if user is admin (either role or is_admin flag)
  const isAdmin = user?.role === "superadmin" || user?.role === "admin" || user?.is_admin === true || user?.is_admin === "true";
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9BD5]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (requireAdmin && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Redirect to setup wizard if onboarding not completed (for non-admin users)
  if (requireOnboarding && !isAdmin && onboardingCompleted === false) {
    return <Navigate to="/setup" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  // Check if user is admin
  const isAdmin = user?.role === "superadmin" || user?.role === "admin" || user?.is_admin === true || user?.is_admin === "true";
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#5B9BD5]"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to={isAdmin ? "/admin" : "/dashboard"} replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/privacidad" element={<PrivacyPage />} />
      <Route path="/terminos" element={<TermsPage />} />
      <Route path="/contacto" element={<ContactPage />} />
      <Route path="/developers" element={<DeveloperPortal />} />
      
      {/* Setup Wizard - Protected but doesn't require onboarding */}
      <Route path="/setup" element={<ProtectedRoute requireOnboarding={false}><SetupWizard /></ProtectedRoute>} />
      
      {/* Company Dashboard routes - Requires onboarding */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<CompanyDashboard />} />
        <Route path="invoices" element={<InvoicesPage />} />
        <Route path="invoices/new" element={<CreateInvoicePage />} />
        <Route path="quotes" element={<QuotesPage />} />
        <Route path="carta-porte" element={<CartaPortePage />} />
        <Route path="clients" element={<ClientsPage />} />
        <Route path="credit-notes" element={<CreditNotesPage />} />
        <Route path="payment-complements" element={<PaymentComplementsPage />} />
        <Route path="reports" element={<ReportsPage />} />
        <Route path="analytics" element={<AnalyticsPage />} />
        <Route path="recurring" element={<RecurringPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="api-keys" element={<ApiKeysPage />} />
        <Route path="support" element={<SupportPage />} />
        <Route path="tax-config" element={<TaxConfigPage />} />
        <Route path="certificates" element={<CertificatesPage />} />
        <Route path="multi-rfc" element={<MultiRfcPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="subscription" element={<SubscriptionPage />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="pdf-customizer" element={<PdfCustomizerPage />} />
        <Route path="referrals" element={<ReferralProgram />} />
        <Route path="api-dashboard" element={<ApiDashboard />} />
      </Route>
      
      {/* Admin routes */}
      <Route path="/admin" element={<ProtectedRoute requireAdmin><DashboardLayout isAdmin /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="companies" element={<AdminCompaniesPage />} />
        <Route path="tickets" element={<AdminTicketsPage />} />
        <Route path="config" element={<AdminConfigPage />} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
